import { FunctionComponent, HTMLAttributes } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { useConfig } from "./providers/ConfigProvider";
import { WidgetButton } from "./components/WidgetButton";
import { PopupMessage } from "./components/PopupMessage";
import { ModeSelectionMenu } from "./components/ModeSelectionMenu";
import { ChatContent } from "./components/ChatContent";
import { VoiceContent } from "./components/VoiceContent";
import { useUnifiedWidget } from "./providers/UnifiedWidgetProvider";
import FocusTrap from "./FocusTrap";
import { ConversationProvider } from "@elevenlabs/react";

type UnifiedWidgetContainerProps = HTMLAttributes<HTMLDivElement> & {
  position: string;
  zIndex: number;
  textColor: string;
}
const UnifiedWidgetContainer = (
  { position, zIndex, textColor, children, ref, ...props }: UnifiedWidgetContainerProps
) => {
  const { gooberCss } = useUnifiedWidget();

  return (
    <div 
      ref={ref}
      {...props}
      className={gooberCss({
        position: "fixed",
        display: "flex",
        flexDirection: "column",
        alignItems: position === "bottom-right" ? "flex-end" : "flex-start",
        bottom: '15px',
        right: position === "bottom-right" ? '15px' : undefined,
        left: position === "bottom-left" ? '15px' : undefined,
        fontSize: '16px',
        zIndex: zIndex,
        maxWidth: "calc(100% - 30px)",
        maxHeight: "calc(100% - 30px)",
        color: textColor,
      })}
    >
      {children}
    </div>
  )
}

type ContentContainerProps = HTMLAttributes<HTMLDivElement> & {
  width: number;
  height: number;
  open: boolean;
}
const ContentContainer = ({ width, height, open, children, ...props }: ContentContainerProps) => {
  const { gooberCss } = useUnifiedWidget();
  
  return (
    <div 
      {...props}
      className={gooberCss({
        position: "absolute",
        maxWidth: open ? `${width}px` : "0px",
        maxHeight: open ? `${height}px` : "0px",
        bottom: "100%",
        pointerEvents: open ? "auto" : "none",
      })}
    >
      {children}
    </div>
  )
  
};

export const UnifiedWidget: FunctionComponent = () => {
  const { config } = useConfig();
  const { updateState, open } = useUnifiedWidget();
  const widgetContainer = useRef<HTMLDivElement | null>(null);
  const widgetButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    function handleEscPress(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        e.stopPropagation();
        updateState({ open: false, mode: 'menu' });
      }
    }
    widgetContainer.current?.addEventListener('keydown', handleEscPress);
    return () => {
      widgetContainer.current?.removeEventListener('keydown', handleEscPress);
    }
  }, [widgetContainer, widgetButton, updateState, open]);

  const { position, zIndex, width, height, textColor } = config;

  return (
    <FocusTrap active={open}>
      <UnifiedWidgetContainer
        position={position}
        zIndex={zIndex}
        textColor={textColor}
        ref={widgetContainer}
      >
        <ContentContainer width={width} height={height} open={open}>
          {/* Mode selection menu - shown when mode is 'menu' */}
          <ModeSelectionMenu />

          {/* Chat content - shown when mode is 'chat' */}
          {open && <ChatContent />}
          
          {/* Voice content - shown when mode is 'voice' (renders in fixed position) */}
          {open && (
            <ConversationProvider>
              <VoiceContent />
            </ConversationProvider>
          )}
        </ContentContainer>
        
        <PopupMessage />

        {/* Widget button - always visible */}
        <WidgetButton ref={widgetButton} />
      </UnifiedWidgetContainer>
    </FocusTrap>
  )
}
